import type { IMeasuresResponse } from "../../scheme/components/analytics/interfaces";
import type {
  IMultiAnalysisDataMark,
  IMultiAnalysisTimeRange,
  IStructureTreePoint,
  MultiAnalysisTimeValue,
} from "../types";
import { parseMultiAnalysisChartTime } from "./range-frame";
import { hasMeasureFrequencyBand, measureBandMatchesFrequencyFilter } from "./template-filters";

const isFrequencyRangeFilterKey = (key: string): boolean => {
  const normalized = key.toLowerCase();

  return normalized === "param1" || normalized === "param2";
};

const toTimestamp = (value: MultiAnalysisTimeValue): number | null => {
  if (!value) {
    return null;
  }

  const timestamp = parseMultiAnalysisChartTime(value);

  return Number.isFinite(timestamp) ? timestamp : null;
};

const hasFilterValue = (value: unknown): boolean =>
  Array.isArray(value)
    ? value.some(hasFilterValue)
    : value !== null && value !== undefined && String(value).trim() !== "";

const getFilterSourceValue = (source: Record<string, unknown>, key: string): unknown => {
  if (Object.prototype.hasOwnProperty.call(source, key)) {
    return source[key];
  }

  const normalizedKey = key.toLowerCase();
  const sourceKey = Object.keys(source).find(item => item.toLowerCase() === normalizedKey);

  return sourceKey ? source[sourceKey] : undefined;
};

const isEqualFilterValue = (sourceValue: unknown, filterValue: unknown): boolean => {
  if (!hasFilterValue(filterValue)) {
    return true;
  }

  if (Array.isArray(filterValue)) {
    return filterValue.some(value => isEqualFilterValue(sourceValue, value));
  }

  if (sourceValue === null || sourceValue === undefined) {
    return false;
  }

  const sourceNumber = Number(sourceValue);
  const filterNumber = Number(filterValue);

  if (Number.isFinite(sourceNumber) && Number.isFinite(filterNumber)) {
    return sourceNumber === filterNumber;
  }

  return String(sourceValue) === String(filterValue);
};

export const matchesTemplateFilters = (source: Record<string, unknown>, filters: Record<string, unknown>): boolean => {
  const measureParam1 = getFilterSourceValue(source, "param1") ?? getFilterSourceValue(source, "Param1");
  const measureParam2 = getFilterSourceValue(source, "param2") ?? getFilterSourceValue(source, "Param2");

  if (
    hasMeasureFrequencyBand(measureParam1, measureParam2) &&
    !measureBandMatchesFrequencyFilter(measureParam1, measureParam2, filters)
  ) {
    return false;
  }

  return Object.entries(filters).every(([key, filterValue]) => {
    if (isFrequencyRangeFilterKey(key) || !hasFilterValue(filterValue)) {
      return true;
    }

    const sourceValue = getFilterSourceValue(source, key);

    if (sourceValue === undefined) {
      return true;
    }

    return isEqualFilterValue(sourceValue, filterValue);
  });
};

export const createDataMarkKey = (idMeasure: number, date: string, idPoint: number): string =>
  `${idMeasure}:${date}:${idPoint}`;

export const resolveDataMarkMeasureDate = (mark: IMultiAnalysisDataMark): string => mark.measureDate || mark.date;

export const resolveMeasureDataAlarmStatus = (
  value: number,
  alarmLevel2: number | null,
  alarmLevel3: number | null,
  alarmLevel4: number | null,
): number => {
  if (Number.isFinite(alarmLevel4) && value >= Number(alarmLevel4)) {
    return 90;
  }

  if (Number.isFinite(alarmLevel3) && value >= Number(alarmLevel3)) {
    return 60;
  }

  if (Number.isFinite(alarmLevel2) && value >= Number(alarmLevel2)) {
    return 45;
  }

  return 30;
};

export const isTimestampInRange = (timestamp: number, range: IMultiAnalysisTimeRange, inclusive = true): boolean => {
  const start = toTimestamp(range.start);
  const end = toTimestamp(range.end);

  if (start === null || end === null) {
    return false;
  }

  const from = Math.min(start, end);
  const to = Math.max(start, end);

  if (inclusive) {
    return timestamp >= from && timestamp <= to;
  }

  return timestamp > from && timestamp < to;
};

export const isDateInRange = (date: string, range: IMultiAnalysisTimeRange): boolean => {
  const timestamp = parseMultiAnalysisChartTime(date);

  return Number.isFinite(timestamp) && isTimestampInRange(timestamp, range);
};

export const filterDataMarksInRange = (
  marks: readonly IMultiAnalysisDataMark[],
  range: IMultiAnalysisTimeRange,
): IMultiAnalysisDataMark[] => marks.filter(mark => isDateInRange(mark.date, range));

export const applyActiveMeasureCellFilters = (
  measure: IMeasuresResponse,
  filters: Record<string, unknown>,
): boolean => {
  return matchesTemplateFilters(measure as unknown as Record<string, unknown>, filters);
};

export const buildDataMarksFromMeasureResponse = (
  measures: readonly IMeasuresResponse[],
  points: readonly IStructureTreePoint[],
  filters: Record<string, unknown> = {},
  pointDirectionLabelFormatter?: (direction: number) => string,
  measureDescriptions?: ReadonlyMap<string, string | null>,
): IMultiAnalysisDataMark[] => {
  const pointById = new Map(
    points.map(point => [
      point.id,
      `${point.title}${
        point.direction > 0 && pointDirectionLabelFormatter ? ` ${pointDirectionLabelFormatter(point.direction)}` : ""
      }`,
    ]),
  );

  return measures.flatMap(measure => {
    if (!applyActiveMeasureCellFilters(measure, filters)) {
      return [];
    }

    const pointName = pointById.get(measure.idPoint) ?? String(measure.idPoint);

    return [
      {
        key: createDataMarkKey(measure.idMeasure, measure.Date, measure.idPoint),
        idMeasure: measure.idMeasure,
        date: measure.Date,
        measureDate: measure.Date,
        idPoint: measure.idPoint,
        pointName,
        measureName: measure.Name,
        measureDescription:
          measure.Description === undefined ? measureDescriptions?.get(measure.Name) : measure.Description,
        value: measure.Value1,
        value2: measure.Value2,
        alarmStatus: resolveMeasureDataAlarmStatus(
          measure.Value1,
          measure.AlarmLevel2,
          measure.AlarmLevel3,
          measure.AlarmLevel4,
        ),
        units: measure.Units,
        rotationFrequencyRpm: measure.RPM,
        param1: measure.Param1 ?? null,
        param2: measure.Param2 ?? null,
      },
    ];
  });
};

export const findLastDataMarkInRange = (
  marks: readonly IMultiAnalysisDataMark[],
  range: IMultiAnalysisTimeRange,
): IMultiAnalysisDataMark | null => {
  const filtered = marks
    .filter(mark => isDateInRange(mark.date, range))
    .sort((left, right) => parseMultiAnalysisChartTime(right.date) - parseMultiAnalysisChartTime(left.date));

  return filtered[0] ?? null;
};

export const selectDataMarksInTimeRange = (
  marks: readonly IMultiAnalysisDataMark[],
  anchor: IMultiAnalysisDataMark,
  target: IMultiAnalysisDataMark,
): IMultiAnalysisDataMark[] => {
  const anchorTime = parseMultiAnalysisChartTime(anchor.date);
  const targetTime = parseMultiAnalysisChartTime(target.date);

  if (!Number.isFinite(anchorTime) || !Number.isFinite(targetTime)) {
    return [target];
  }

  const from = Math.min(anchorTime, targetTime);
  const to = Math.max(anchorTime, targetTime);

  return marks.filter(mark => {
    const timestamp = parseMultiAnalysisChartTime(mark.date);

    return Number.isFinite(timestamp) && timestamp >= from && timestamp <= to;
  });
};
